<template>
    <div class="offcanvas offcanvas-end description-sources-panel" tabindex="-1" ref="panel">
        <div class="offcanvas-header border-bottom">
            <div>
                <h5 class="offcanvas-title"><span class="fas fa-magic me-2 text-primary"></span>Vyplnit s pomocí AI</h5>
                <div class="small text-muted">Nahrajte podklady k nemovitosti, AI z nich vyčte hodnoty pro formulář.</div>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Zavřít"></button>
        </div>
        <div class="offcanvas-body">
            <div v-if="errorMessage !== null" class="alert alert-danger d-flex align-items-start gap-2">
                <span class="flex-grow-1">{{ errorMessage }}</span>
                <button type="button" class="btn-close" aria-label="Zavřít" @click="errorMessage = null"></button>
            </div>

            <ol class="small text-muted ps-3 mb-3">
                <li class="mb-1">
                    <strong class="text-body">Přidejte podklady</strong>: výkresy, výpis z&nbsp;katastru, inzerát, text,
                    nahrávku z&nbsp;prohlídky nebo přílohy, které už odhad má. Můžete jich přidat víc najednou,
                    snímek obrazovky vložíte přes {{ pasteShortcut }}.
                </li>
                <li class="mb-1">
                    <strong class="text-body">Pojmenujte je</strong>, třeba „1. NP“ nebo „LV“. Popisek pomůže AI
                    poznat, co podklad popisuje, hlavně u&nbsp;výkresů jednotlivých podlaží.
                </li>
                <li class="mb-1">
                    <strong class="text-body">Spusťte zpracování.</strong> Každý podklad stojí
                    {{ creditCost }} {{ plural(creditCost, 'kredit', 'kredity', 'kreditů') }} a&nbsp;čte se zvlášť,
                    obvykle desítky sekund. Formulář mezitím můžete dál vyplňovat.
                </li>
                <li class="mb-1">
                    <strong class="text-body">Převezměte hodnoty.</strong> V&nbsp;okně zaškrtnete, co převzít. Hodnotu
                    můžete před převzetím opravit, podlahovou plochu z&nbsp;více výkresů poskládat po podlažích. Bez
                    vás se nic nezapíše a&nbsp;převzatá pole zůstanou označená k&nbsp;ověření.
                </li>
                <li>
                    <strong class="text-body">Vraťte se k&nbsp;nim kdykoli</strong>: přes „Hodnoty z&nbsp;podkladů“ nad
                    formulářem nebo přes odkaz „v&nbsp;podkladech N&nbsp;jiné hodnoty“ u&nbsp;pole.
                </li>
            </ol>

            <div class="description-sources-dropzone p-4 text-center mb-2"
                 :class="{'dragover': dragover}"
                 @dragover.prevent="dragover = true"
                 @dragleave="dragover = false"
                 @drop.prevent="onDrop">
                <span class="fas fa-upload fa-2x text-primary mb-2"></span>
                <div>Přetáhněte sem i <strong>více souborů najednou</strong></div>
                <div class="small text-muted mb-2">
                    PDF, fotky, výkresy, txt, zvuk, do 15 MB; snímek obrazovky nebo text vložíte přes {{ pasteShortcut }}
                </div>
                <label class="btn btn-sm btn-primary mb-0">
                    Vybrat soubory
                    <input type="file" multiple class="d-none" :accept="ACCEPTED_FILES" @change="onFilesSelected">
                </label>
            </div>
            <div class="d-flex gap-2 mb-3">
                <button type="button" class="btn btn-sm flex-fill"
                        :class="picker === 'attachments' ? 'btn-secondary' : 'btn-outline-secondary'"
                        @click="togglePicker('attachments')">
                    <span class="fas fa-paperclip me-2"></span>Z příloh odhadu
                </button>
                <button type="button" class="btn btn-sm flex-fill"
                        :class="picker === 'text' ? 'btn-secondary' : 'btn-outline-secondary'"
                        @click="togglePicker('text')">
                    <span class="fas fa-align-left me-2"></span>Vložit text
                </button>
            </div>

            <div v-if="picker === 'attachments'" class="card card-body mb-3">
                <div class="small text-muted mb-2">Soubory, které už odhad má, nic se nenahrává znovu.</div>
                <div v-if="attachments.length === 0" class="small text-muted">Odhad zatím nemá žádné přílohy.</div>
                <div v-for="attachment in attachments" :key="attachment.id" class="form-check">
                    <input class="form-check-input" type="checkbox"
                           :id="'description-source-attachment-' + attachment.id"
                           :value="attachment.id"
                           :disabled="usedAttachmentIds.has(attachment.id)"
                           v-model="selectedAttachmentIds">
                    <label class="form-check-label" :for="'description-source-attachment-' + attachment.id">
                        {{ attachmentName(attachment) }}
                        <span v-if="usedAttachmentIds.has(attachment.id)" class="small text-muted">, už v podkladech</span>
                    </label>
                </div>
                <button type="button" class="btn btn-sm btn-outline-primary mt-2 align-self-start"
                        :disabled="selectedAttachmentIds.length === 0"
                        @click="queueAttachments">Přidat vybrané</button>
            </div>

            <div v-if="picker === 'text'" class="card card-body mb-3">
                <textarea class="form-control mb-2" rows="4" v-model="text"
                          placeholder="Např. text inzerátu nebo poznámky z prohlídky"></textarea>
                <button type="button" class="btn btn-sm btn-outline-primary align-self-start"
                        :disabled="text.trim() === ''"
                        @click="queueText">Přidat text</button>
            </div>

            <template v-if="queue.length > 0">
                <h6 class="text-muted text-uppercase small fw-semibold">Připraveno ke zpracování</h6>
                <div class="list-group mb-2">
                    <div v-for="(item, index) in queue" :key="item.key" class="list-group-item d-flex align-items-center gap-2">
                        <div class="d-flex flex-column">
                            <button type="button" class="btn btn-sm btn-link p-0" :disabled="index === 0"
                                    aria-label="Posunout nahoru" @click="moveQueued(index, -1)">
                                <span class="fas fa-chevron-up"></span>
                            </button>
                            <button type="button" class="btn btn-sm btn-link p-0" :disabled="index === queue.length - 1"
                                    aria-label="Posunout dolů" @click="moveQueued(index, 1)">
                                <span class="fas fa-chevron-down"></span>
                            </button>
                        </div>
                        <span :class="queuedIcon(item)" class="text-secondary"></span>
                        <div class="flex-grow-1 small text-truncate">{{ item.name }}</div>
                        <input class="form-control form-control-sm w-25" placeholder="popisek, např. 1. NP" v-model="item.source.label">
                        <button type="button" class="btn btn-sm btn-link text-danger" aria-label="Odebrat" @click="queue.splice(index, 1)">
                            <span class="fas fa-times"></span>
                        </button>
                    </div>
                </div>
                <div class="small text-muted mb-2">Popisek AI dostane jako nápovědu, co podklad popisuje.</div>
                <button type="button" class="btn btn-success w-100 mb-1" :disabled="processing" @click="processQueue">
                    <span v-if="processing" class="spinner-border spinner-border-sm me-2 align-middle" role="status"></span>
                    Zpracovat {{ queue.length }} {{ plural(queue.length, 'podklad', 'podklady', 'podkladů') }},
                    {{ queueCost }} {{ plural(queueCost, 'kredit', 'kredity', 'kreditů') }}
                </button>
                <div class="small mb-3" :class="creditBalance < queueCost ? 'text-danger' : 'text-muted'">
                    Zůstatek {{ creditBalance }} {{ plural(creditBalance, 'kredit', 'kredity', 'kreditů') }}
                </div>
            </template>

            <h6 class="text-muted text-uppercase small fw-semibold">Podklady odhadu</h6>
            <div v-if="batch.total > 0 && batch.processing" class="card mb-2"
                 :class="batchIsSlow ? 'border-warning-subtle bg-warning-subtle' : 'description-sources-progress-card'">
                <div class="card-body d-flex gap-3 p-3">
                    <div class="description-sources-progress-art" aria-hidden="true">
                        <div class="sheet"></div>
                        <div class="sheet"></div>
                        <div class="sheet"><div class="line"></div><div class="line"></div><div class="line"></div><div class="line"></div><div class="beam"></div></div>
                        <span class="fas fa-magic spark"></span>
                    </div>
                    <div class="flex-grow-1">
                        <div class="fw-semibold">AI čte podklady, hotovo {{ batch.done }} z {{ batch.total }}</div>
                        <div class="progress my-2 description-sources-progress" role="progressbar"
                             :aria-valuenow="batch.done" aria-valuemin="0" :aria-valuemax="batch.total">
                            <div class="progress-bar progress-bar-striped progress-bar-animated"
                                 :style="{width: Math.max(batch.done / batch.total * 100, 5) + '%'}"></div>
                        </div>
                        <div v-if="batchIsSlow" class="small text-muted">
                            Trvá to déle než obvykle, zpracování ale pořád běží. Když skončí chybou, podklad zkusíte
                            znovu a&nbsp;kredity se za něj znovu nestrhnou.
                        </div>
                        <div v-else-if="batch.audio" class="small text-muted">
                            Nahrávku z&nbsp;prohlídky AI nejdřív přepíše do textu a&nbsp;pak z&nbsp;něj vyčte hodnoty.
                            <strong class="text-body">Podle délky nahrávky trvá celé čtení obvykle 5–10 minut.</strong>
                        </div>
                        <div v-else class="small text-muted">
                            Z&nbsp;každého podkladu AI vyčítá plochy, dispozici, konstrukci a&nbsp;další hodnoty formuláře.
                            Podle složitosti trvá jeden podklad obvykle 1–3 minuty, podrobné výkresy a&nbsp;delší PDF
                            i&nbsp;déle. Podklady se čtou souběžně. <strong class="text-body">Celé čtení obvykle trvá
                            2–5 minut.</strong>
                        </div>
                        <div v-if="!batchIsSlow" class="small text-muted mt-1">
                            <span class="fas fa-info-circle me-1"></span>Panel můžete zavřít a&nbsp;vyplňovat dál, čtení poběží.
                        </div>
                    </div>
                </div>
            </div>
            <div v-else-if="batch.total > 0" class="card border-success-subtle bg-success-subtle mb-2">
                <div class="card-body d-flex align-items-center gap-3 p-3">
                    <span class="fas fa-check-circle fa-2x text-success"></span>
                    <div class="flex-grow-1">
                        <div class="fw-semibold">
                            Podklady jsou přečtené, AI našla {{ batch.parameterCount }}
                            {{ plural(batch.parameterCount, 'hodnotu', 'hodnoty', 'hodnot') }}
                        </div>
                        <div class="small text-muted">
                            <template v-if="batch.failed > 0">
                                {{ batch.failed }} {{ plural(batch.failed, 'podklad', 'podklady', 'podkladů') }} se
                                nepodařilo přečíst, zkuste {{ batch.failed === 1 ? 'ho' : 'je' }} znovu v&nbsp;menu u&nbsp;řádku.
                            </template>
                            <template v-else>Do formuláře se zapíší až po vašem převzetí.</template>
                        </div>
                    </div>
                    <button v-if="batch.parameterCount > 0" type="button" class="btn btn-sm btn-success text-nowrap"
                            @click="$emit('show-found-values', null)">Převzít hodnoty</button>
                </div>
            </div>
            <div v-if="loading" class="text-muted small">
                <span class="spinner-border spinner-border-sm me-2 align-middle" role="status"></span>Načítání…
            </div>
            <div v-else-if="sources.length === 0" class="text-muted small">Odhad zatím nemá žádné podklady.</div>
            <div v-else class="list-group">
                <div v-for="(source, index) in sources" :key="source.id" class="list-group-item d-flex align-items-center gap-2">
                    <div class="d-flex flex-column">
                        <button type="button" class="btn btn-sm btn-link p-0" :disabled="index === 0"
                                aria-label="Posunout nahoru" @click="moveSource(source, index - 1)">
                            <span class="fas fa-chevron-up"></span>
                        </button>
                        <button type="button" class="btn btn-sm btn-link p-0" :disabled="index === sources.length - 1"
                                aria-label="Posunout dolů" @click="moveSource(source, index + 1)">
                            <span class="fas fa-chevron-down"></span>
                        </button>
                    </div>
                    <span :class="sourceIcon(source)" class="text-secondary"></span>
                    <div class="flex-grow-1 small min-width-0">
                        <div class="fw-semibold text-truncate">{{ source.label !== '' ? source.label : sourceName(source) }}</div>
                        <div v-if="source.label !== ''" class="text-muted text-truncate">{{ sourceName(source) }}</div>
                    </div>
                    <div class="small text-nowrap">
                        <span v-if="source.state === 'processing'" class="text-primary">
                            <span class="spinner-border spinner-border-sm me-1 align-middle" role="status"></span>Čte se…
                        </span>
                        <span v-else-if="source.state === 'error'" class="text-danger">
                            <span class="fas fa-exclamation-triangle me-2"></span>Chyba
                        </span>
                        <span v-else-if="source.parameterCount !== null" class="text-success">
                            <span class="fas fa-check me-2"></span>{{ source.parameterCount }}
                            {{ plural(source.parameterCount, 'hodnota', 'hodnoty', 'hodnot') }}
                        </span>
                    </div>
                    <div class="dropdown">
                        <button type="button" class="btn btn-sm btn-link text-secondary" data-bs-toggle="dropdown" aria-label="Akce">
                            <span class="fas fa-ellipsis-v"></span>
                        </button>
                        <ul class="dropdown-menu dropdown-menu-end">
                            <li v-if="source.state === 'error'">
                                <button type="button" class="dropdown-item" @click="retrySource(source)">
                                    <span class="fas fa-redo me-2"></span>Zkusit znovu
                                </button>
                            </li>
                            <li v-if="source.state === 'completed' && (source.parameterCount ?? 0) > 0">
                                <button type="button" class="dropdown-item" @click="$emit('show-found-values', source.id)">
                                    <span class="fas fa-magic me-2"></span>Hodnoty z tohoto podkladu
                                </button>
                            </li>
                            <li v-if="source.url !== null">
                                <a class="dropdown-item" :href="source.url" target="_blank" rel="noopener">
                                    <span class="fas fa-external-link-alt me-2"></span>Zobrazit podklad
                                </a>
                            </li>
                            <li>
                                <button type="button" class="dropdown-item text-danger" @click="removeSource(source)">
                                    <span class="fas fa-trash me-2"></span>Odstranit podklad
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {plural} from '@/components/estimates/common/photos/texts';
import {Attachment} from '@/model/api';
import EstimateDescriptionSources, {
    DescriptionSource,
    NewDescriptionSource,
} from '@/model/estimates/EstimateDescriptionSources';
import {ErrorWithUserMessage} from '@/model/errors/ErrorWithUserMessage';
import {requestAiConsent} from '@/naja/aiConsent';
import {Offcanvas} from 'bootstrap';
import {computed, defineComponent, onBeforeUnmount, onMounted, PropType, ref} from 'vue';
import {useI18n} from 'vue-i18n';

interface QueuedSource {
    key: number;
    name: string;
    source: NewDescriptionSource;
}

/** The state of the sources is checked this often while some are processed */
const POLLING_INTERVAL = 3000;
/** A failed check is tried again after a longer while */
const POLLING_RETRY_INTERVAL = 10000;

const ACCEPTED_FILES = '.pdf,.png,.jpg,.jpeg,.webp,.txt,.mp3,.m4a,.webm';

/** Reading the sources takes longer than usual after this while, a recording takes longer */
const USUAL_BATCH_DURATION = 5 * 60 * 1000;
const USUAL_AUDIO_BATCH_DURATION = 10 * 60 * 1000;

export default defineComponent({
    props: {
        sourcesClient: {
            type: Object as PropType<EstimateDescriptionSources>,
            required: true,
        },
        /** The attachments of the estimate when the form was opened */
        initialAttachments: {
            type: Array as PropType<Attachment[]>,
            required: true,
        },
        /** Loads the current attachments of the estimate, they may change while the form is open */
        loadAttachments: {
            type: Function as PropType<() => Promise<Attachment[]>>,
            required: true,
        },
    },
    emits: ['sources-changed', 'show-found-values'],
    setup(props, {emit, expose}) {
        const {t} = useI18n();

        const panel = ref<HTMLElement | null>(null);
        let panelInstance: Offcanvas | null = null;

        const sources = ref<DescriptionSource[]>([]);
        const creditBalance = ref(0);
        const creditCost = ref(1);
        const loading = ref(true);
        const processing = ref(false);
        const errorMessage = ref<string | null>(null);

        const attachments = ref<Attachment[]>([]);
        const selectedAttachmentIds = ref<string[]>([]);
        const text = ref('');
        const picker = ref<'attachments' | 'text' | null>(null);
        const dragover = ref(false);

        const queue = ref<QueuedSource[]>([]);
        let nextQueueKey = 1;

        let pollingTimer: number | null = null;

        const usedAttachmentIds = computed(() => new Set([
            ...sources.value.map(source => source.attachmentId),
            ...queue.value.map(item => item.source.kind === 'attachment' ? item.source.attachmentId : null),
        ].filter((id): id is string => id !== null)));

        const queueCost = computed(() => queue.value.length * creditCost.value);

        // the sources read together, from the first one seen processing until the panel is closed
        const batchIds = ref<string[]>([]);
        const batchStartedAt = ref(0);
        const now = ref(Date.now());

        const batch = computed(() => {
            const batchSources = sources.value.filter(source => batchIds.value.includes(source.id));
            const processingSources = batchSources.filter(source => source.state === 'processing');

            return {
                total: batchSources.length,
                done: batchSources.length - processingSources.length,
                processing: processingSources.length > 0,
                audio: processingSources.some(source => source.type === 'audio'),
                failed: batchSources.filter(source => source.state === 'error').length,
                parameterCount: batchSources.reduce((sum, source) => sum + (source.parameterCount ?? 0), 0),
            };
        });

        const batchIsSlow = computed(() => now.value - batchStartedAt.value
            > (batch.value.audio ? USUAL_AUDIO_BATCH_DURATION : USUAL_BATCH_DURATION));

        function trackBatch(current: DescriptionSource[]): void {
            const processingIds = current.filter(source => source.state === 'processing').map(source => source.id);

            now.value = Date.now();

            if (processingIds.length === 0) {
                return;
            }

            if (!batch.value.processing) {
                batchIds.value = [];
                batchStartedAt.value = now.value;
            }

            batchIds.value = [...new Set([...batchIds.value, ...processingIds])];
        }

        function show(): void {
            panelInstance?.show();
            void refreshAttachments();
        }

        function setSources(response: {sources: DescriptionSource[], creditBalance: number, creditCost: number}): void {
            trackBatch(response.sources);
            sources.value = response.sources;
            creditBalance.value = response.creditBalance;
            creditCost.value = response.creditCost;

            emit('sources-changed', response.sources, sourceNames());
            schedulePolling();
        }

        function schedulePolling(delay: number = POLLING_INTERVAL): void {
            if (pollingTimer !== null) {
                window.clearTimeout(pollingTimer);
                pollingTimer = null;
            }

            if (sources.value.some(source => source.state === 'processing')) {
                pollingTimer = window.setTimeout(() => void poll(), delay);
            }
        }

        // a failed check, e.g. during a deploy or with the connection lost, is tried again later
        async function poll(): Promise<void> {
            if (!await run(() => props.sourcesClient.load())) {
                schedulePolling(POLLING_RETRY_INTERVAL);
            }
        }

        /**
         * Runs the request and shows the sources it returns, or the message of its failure
         */
        async function run(request: () => Promise<{sources: DescriptionSource[], creditBalance: number, creditCost: number}>): Promise<boolean> {
            try {
                setSources(await request());
                errorMessage.value = null;

                return true;
            } catch (error) {
                errorMessage.value = error instanceof ErrorWithUserMessage
                    ? error.message
                    : 'Podklady se nepodařilo načíst nebo uložit, zkuste to prosím znovu.';

                return false;
            }
        }

        function setAttachments(current: Attachment[]): void {
            attachments.value = [...current]
                .sort((a, b) => attachmentName(a).localeCompare(attachmentName(b), 'cs') || a.position - b.position);
        }

        async function refreshAttachments(): Promise<void> {
            try {
                setAttachments(await props.loadAttachments());
            } catch {
                attachments.value = [];
            }
        }

        function attachmentName(attachment: Attachment): string {
            const sameType = attachments.value.filter(current => current.type === attachment.type);
            const typeName = t(`parameters.attachments.options.${attachment.type}`);
            const name = typeName.charAt(0).toUpperCase() + typeName.slice(1);

            return sameType.length > 1 ? `${name} (${attachment.position + 1})` : name;
        }

        function togglePicker(name: 'attachments' | 'text'): void {
            picker.value = picker.value === name ? null : name;
        }

        function addToQueue(name: string, source: NewDescriptionSource): void {
            queue.value.push({key: nextQueueKey++, name, source});
        }

        function queueFiles(files: FileList | File[]): void {
            Array.from(files).forEach(file => addToQueue(file.name, {kind: 'file', label: '', file}));
        }

        function onFilesSelected(event: Event): void {
            const input = event.target as HTMLInputElement;

            if (input.files !== null) {
                queueFiles(input.files);
            }

            input.value = '';
        }

        function onDrop(event: DragEvent): void {
            dragover.value = false;

            if (event.dataTransfer !== null) {
                queueFiles(event.dataTransfer.files);
            }
        }

        const pasteShortcut = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘V' : 'Ctrl+V';

        function isEditable(target: EventTarget | null): boolean {
            return target instanceof HTMLElement
                && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
        }

        /**
         * A screenshot pasted anywhere in the form opens the panel with it, a field takes no image anyway;
         * a text is pasted in the open panel only, a text pasted into a field stays there
         */
        function onPaste(event: ClipboardEvent): void {
            if (event.clipboardData === null || (event.target instanceof HTMLElement && event.target.isContentEditable)) {
                return;
            }

            const files = Array.from(event.clipboardData.files);

            if (files.length > 0) {
                event.preventDefault();

                const time = new Date().toLocaleTimeString('cs-CZ').replace(/:/g, '-');
                // a screenshot comes as "image.png"
                queueFiles(files.map((file, index) => file.name === 'image.png'
                    ? new File([file], `Vložený obrázek ${time}${files.length > 1 ? '-' + (index + 1) : ''}.png`, {type: file.type})
                    : file));

                if (!isShown()) {
                    show();
                }

                return;
            }

            const pasted = event.clipboardData.getData('text/plain').trim();

            if (pasted !== '' && isShown() && !isEditable(event.target)) {
                event.preventDefault();
                text.value = pasted;
                queueText();
            }
        }

        function queueAttachments(): void {
            attachments.value
                .filter(attachment => selectedAttachmentIds.value.includes(attachment.id))
                .forEach(attachment => addToQueue(
                    attachmentName(attachment),
                    {kind: 'attachment', label: '', attachmentId: attachment.id},
                ));

            selectedAttachmentIds.value = [];
            picker.value = null;
        }

        function queueText(): void {
            const content = text.value.trim();
            const preview = content.length > 40 ? content.slice(0, 40) + '…' : content;

            addToQueue('Text: ' + preview, {kind: 'text', label: '', text: content});

            text.value = '';
            picker.value = null;
        }

        function moveQueued(index: number, direction: -1 | 1): void {
            const items = queue.value;
            [items[index], items[index + direction]] = [items[index + direction], items[index]];
        }

        async function processQueue(): Promise<void> {
            if (!await requestAiConsent()) {
                return;
            }

            processing.value = true;

            if (await run(() => props.sourcesClient.add(queue.value.map(item => item.source)))) {
                queue.value = [];
            }

            processing.value = false;
        }

        async function moveSource(source: DescriptionSource, position: number): Promise<void> {
            await run(() => props.sourcesClient.move(source.id, position));
        }

        async function retrySource(source: DescriptionSource): Promise<void> {
            if (!await requestAiConsent()) {
                return;
            }

            await run(() => props.sourcesClient.retry(source.id));
        }

        async function removeSource(source: DescriptionSource): Promise<void> {
            const name = source.label !== '' ? source.label : sourceName(source);

            if (!window.confirm(`Odstranit podklad ${name}? Hodnoty už uložené ve formuláři zůstanou.`)) {
                return;
            }

            await run(() => props.sourcesClient.remove(source.id));
        }

        function sourceName(source: DescriptionSource): string {
            if (source.attachmentId !== null) {
                const attachment = attachments.value.find(current => current.id === source.attachmentId);

                return attachment !== undefined ? attachmentName(attachment) : 'Příloha odhadu';
            }

            if (source.fileName !== null) {
                return source.fileName;
            }

            return source.type === 'text' ? 'Text' : 'Soubor';
        }

        function fileIcon(fileName: string): string {
            if (/\.pdf$/i.test(fileName)) {
                return 'far fa-file-pdf';
            }

            if (/\.(jpe?g|png|webp)$/i.test(fileName)) {
                return 'far fa-file-image';
            }

            if (/\.(mp3|m4a|webm)$/i.test(fileName)) {
                return 'fas fa-microphone';
            }

            return 'far fa-file-alt';
        }

        function queuedIcon(item: QueuedSource): string {
            switch (item.source.kind) {
                case 'file':
                    return fileIcon(item.source.file.name);
                case 'text':
                    return 'fas fa-align-left';
                case 'attachment':
                    return 'fas fa-paperclip';
            }
        }

        function sourceIcon(source: DescriptionSource): string {
            if (source.attachmentId !== null) {
                return 'fas fa-paperclip';
            }

            if (source.fileName !== null) {
                return fileIcon(source.fileName);
            }

            return source.type === 'text' ? 'fas fa-align-left' : 'far fa-file';
        }

        /**
         * Names of the sources by their ids, in their order
         */
        function sourceNames(): Record<string, string> {
            return Object.fromEntries(sources.value.map(source => [
                source.id,
                source.label !== '' ? source.label : sourceName(source),
            ]));
        }

        function hide(): void {
            panelInstance?.hide();
        }

        // the finished reading is told until the panel is closed
        function onHidden(): void {
            if (!batch.value.processing) {
                batchIds.value = [];
            }
        }

        function isShown(): boolean {
            return panel.value?.classList.contains('show') === true;
        }

        onMounted(async () => {
            panelInstance = new Offcanvas(panel.value as HTMLElement);
            panel.value?.addEventListener('hidden.bs.offcanvas', onHidden);
            document.addEventListener('paste', onPaste);

            // the names of the attachments name the sources, the panel loads them again when shown
            setAttachments(props.initialAttachments);
            await run(() => props.sourcesClient.load());
            loading.value = false;
        });

        onBeforeUnmount(() => {
            document.removeEventListener('paste', onPaste);
            panel.value?.removeEventListener('hidden.bs.offcanvas', onHidden);

            if (pollingTimer !== null) {
                window.clearTimeout(pollingTimer);
            }

            panelInstance?.dispose();
        });

        expose({show, hide, isShown});

        return {
            ACCEPTED_FILES,
            pasteShortcut,
            panel,
            show,
            hide,
            isShown,
            sources,
            creditBalance,
            creditCost,
            loading,
            processing,
            errorMessage,
            attachments,
            selectedAttachmentIds,
            usedAttachmentIds,
            text,
            picker,
            dragover,
            queue,
            queueCost,
            batch,
            batchIsSlow,
            plural,
            attachmentName,
            togglePicker,
            onFilesSelected,
            onDrop,
            queueAttachments,
            queueText,
            moveQueued,
            processQueue,
            moveSource,
            retrySource,
            removeSource,
            sourceName,
            queuedIcon,
            sourceIcon,
        };
    },
});
</script>
